'use client';

import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../../../../lib/utils';
import { ArrowDropDown, ArrowDropUp } from '@crabs-ai/web-icons';

export type StepperItemDirection = 'up' | 'down';

export interface StepperItemProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  /** 스테퍼 아이템의 방향 (up: 증가, down: 감소) */
  direction: StepperItemDirection;
}

/**
 * StepperItem - 숫자를 증가/감소시키는 작은 버튼 컴포넌트
 *
 * 16x16 크기의 버튼으로, 위/아래 화살표 아이콘을 표시합니다.
 * Stepper 컴포넌트와 함께 사용되어 숫자 입력을 제어합니다.
 */
const StepperItem = forwardRef<HTMLButtonElement, StepperItemProps>(
  ({ direction, className, disabled, ...props }, ref) => {
    const Icon = direction === 'up' ? ArrowDropUp : ArrowDropDown;

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={cn(
          // 기본 스타일: 16x16, 라운드 4px
          'flex size-[16px] items-center justify-center',
          'overflow-hidden rounded-[4px]',
          // 배경색 상태
          'bg-comp-stepper-bg-default',
          'hover:bg-comp-stepper-bg-hover',
          'active:bg-comp-stepper-bg-active',
          // 비활성화 상태
          'disabled:cursor-not-allowed disabled:[&_path]:fill-comp-stepper-icon-disabled',
          // transition
          'transition-colors',
          className,
        )}
        {...props}
      >
        <Icon className="size-[16px] [&_path]:fill-comp-stepper-icon-default" />
      </button>
    );
  },
);

StepperItem.displayName = 'StepperItem';

export default StepperItem;
